import { modelData } from 'app/lib/modelData'
import { graphql } from 'app/generated/gql'
import { withZod } from '@remix-validated-form/with-zod'
import { ReactElement, useState } from 'react'
import { z } from 'zod'

import Button from 'app/components/Button'
import { FormMarkDownEditor } from 'app/components/form/FormMarkDownEditor'
import { SmartForm } from 'app/components/form/SmartForm'
import { NoteTemplatePicker } from 'app/components/note-templates/NoteTemplatePicker'
import type { ActivityJson } from 'app/types/entities'
import type { ContactJson } from 'app/types/entities'
import { substituteTokens } from 'app/lib/messageTemplates'
import { closeModal } from 'app/lib/modal'
import { NOTE_MAX_LENGTH, NOTE_TOO_LONG_MESSAGE } from 'app/lib/noteMaxLength'
import { notifyError, notifySuccess } from 'app/services/notify.client'
import { gqlMutation } from 'app/lib/graphqlClient'
import type { NoteTemplate } from 'app/generated/graphql'
import { logError } from 'lib/logging'

const ACTIVITY_FIELDS = `id type description activityDate source owner { id name } contacts { id name } locationId orgId workspaceId`

const CREATE_NOTE_MUTATION = graphql(`
  mutation CreateNote($input: CreateActivityInput!) {
    createNote(input: $input) {
      id
      type
      description
      activityDate
      source
      owner {
        id
        name
      }
      contacts {
        id
        name
      }
      locationId
      orgId
      workspaceId
    }
  }
`)

const schema = z.object({
  type: z.string().min(1, { message: `Activity type required` }),
  contactId: z.string().min(1, { message: `Contact Id required` }),
})

export const noteValidator = withZod(
  schema.extend({
    description: z
      .string()
      .min(1, { message: `Description cannot be empty` })
      .max(NOTE_MAX_LENGTH, { message: NOTE_TOO_LONG_MESSAGE }),
  }),
)

/**
 * Modal component to create a new note for a contact
 * @param contact - The contact to create the note for
 * @param onActivityAdded - Optional callback when the note is successfully created
 */
export function CreateNoteModal({
  contact,
  onActivityAdded,
}: {
  contact: ContactJson
  onActivityAdded?: (activity: ActivityJson) => void
}): ReactElement {
  const [isSaving, setIsSaving] = useState(false)
  // The editor only reads its text on mount, so applying a template remounts it (keyed on count).
  const [applied, setApplied] = useState<{ count: number; body?: string }>({ count: 0 })

  const applyTemplate = (template: NoteTemplate) => {
    const { text } = substituteTokens(template.body ?? ``, { contact }, { keepUnresolved: true })
    setApplied((prev) => ({ count: prev.count + 1, body: text }))
  }

  const submitNote = async (values: Record<string, unknown>) => {
    setIsSaving(true)
    const result = await gqlMutation(CREATE_NOTE_MUTATION, {
      input: { type: `Note`, contactId: values.contactId as string, description: values.description as string },
    })

    if (result.error) {
      notifyError(`Error: Failed to create note`)
      logError(`Error saving note`, result.error.message)
      setIsSaving(false)
      return
    }

    const activity = modelData(result.data?.createNote)
    if (activity) {
      // The drawer switches on whichever chip reveals a newly added activity,
      // so nothing has to be nudged into the URL here.
      onActivityAdded?.(activity)

      notifySuccess(`Note added successfully`)
      closeModal()
    } else {
      notifyError(`Error: Failed to create note`)
      logError(`Error saving note`, `No valid response from mutation`)
      setIsSaving(false)
    }
  }

  return (
    <SmartForm
      id="note-form"
      className="w-full md:w-[600px] !gap-y-1 px-6"
      validator={noteValidator}
      hiddenInputs={[
        { name: `type`, value: `Note` },
        { name: `contactId`, value: contact?.id },
      ]}
      onSubmit={submitNote as never}
      onCancel={closeModal}
      preventSubmit
      hideButtons
    >
      <FormMarkDownEditor
        key={applied.count}
        richTextEditorNamespace="Add-Note"
        name="description"
        placeholder="Type your note here..."
        errorClassName="text-red-500"
        description={applied.body}
        cols={6}
        editable
      />
      <div className="col-span-6 -mx-6 mt-4 flex items-center justify-between rounded-b-xl border-t border-default bg-surface px-6 py-3">
        <NoteTemplatePicker
          trigger={
            <button type="button" className="text-xs text-primary-600 hover:underline">
              Use template
            </button>
          }
          onSelect={applyTemplate}
          side="top"
        />
        <div className="flex items-center gap-2">
          <Button label="Cancel" mode="white" size="md" onClick={closeModal} />
          <Button type="submit" label="Save" mode="primary" loading={isSaving} size="md" />
        </div>
      </div>
    </SmartForm>
  )
}
